import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import client, { API } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: "checking", user: null, impersonating: null });

  const refresh = useCallback(async () => {
    try {
      const { data } = await client.get(`/auth/me`);
      setState({
        status: "authenticated",
        user: data.user,
        impersonating: data.impersonated_dealer || null,
      });
    } catch (err) {
      // 401 (unauthenticated) is the expected state before login. Other errors are logged
      // so they surface in the console instead of being silently swallowed.
      if (err?.response?.status !== 401) {
        console.warn("AuthContext.refresh failed:", err);
      }
      setState({ status: "anonymous", user: null, impersonating: null });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await client.post(`/auth/login`, { email, password });
    // Cookie is set by the server; we just refresh our view.
    setState({ status: "authenticated", user: data.user, impersonating: null });
    return data.user;
  }, []);

  const logout = useCallback(() => {
    // Full-page navigation (not axios) — if this session came from SSO, the
    // backend redirects on to Keycloak's own logout endpoint, which needs a
    // real top-level browser navigation to clear Keycloak's session cookie.
    window.location.href = `${API}/auth/sso/logout`;
  }, []);

  const startImpersonation = useCallback(async (dealerId) => {
    const { data } = await client.post(`/impersonation/start/${dealerId}`);
    // The server has swapped our cookie to an impersonation-scoped token.
    setState((s) => ({ ...s, impersonating: data.impersonated_dealer }));
    return data.impersonated_dealer;
  }, []);

  const stopImpersonation = useCallback(async () => {
    await client.post(`/impersonation/stop`);
    setState((s) => ({ ...s, impersonating: null }));
  }, []);

  const updateProfileLocal = useCallback((patch) =>
    setState((s) => ({ ...s, user: { ...s.user, ...patch } })),
  []);

  // Memoise the context value so consumers only re-render when state or handlers change.
  const value = useMemo(
    () => ({ ...state, refresh, login, logout, startImpersonation, stopImpersonation, updateProfileLocal }),
    [state, refresh, login, logout, startImpersonation, stopImpersonation, updateProfileLocal],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
